import isEmpty from 'lodash/isEmpty';
import API from '../api';
import {
    GUEST_BY_ID_ENDPOINT,
    GUEST_COUNTRIES_ENDPOINT,
    GUEST_RESERVATION_ENDPOINT,
    GUEST_RESERVATION_LIST_ENDPOINT,
    GUEST_STATUS_LIST_ENDPOINT,
} from '../endpoints';

export default {
    namespaced: true,

    state() {
        return {
            guest: {
                mobile_phone: '',
                address: {
                    phone: '',
                    work_phone: '',
                    work_phone_ext: '',
                    fax: '',
                },
            },
            reservations: [],
            reservation: null,
            metadata: null,
            detailsTabTitle: {},
            invoicesTab: '',
            statuses: null,
            countries: null,
            states: null,
            provinces: null,
            housekeeping: { comments: null, departure_comments: null },
            notifySelectedGuests: [],
        };
    },

    getters: {
        guest: state => state.guest,
        reservations: state => state.reservations,
        reservation: state => state.reservation,
        metadata: state => state.metadata,
        detailsTabTitle: state => {
            if (state.guest && state.statuses) return detailsTabTitle(state);
            else return '';
        },
        invoicesTab: state => state.invoicesTab,
        statuses: state => state.statuses,
        countries: state => state.countries,
        states: state => state.states,
        provinces: state => state.provinces,
        housekeeping: state => {
            if (state.reservation?.housekeeping?.length) {
                return state.reservation.housekeeping[0];
            }
            return state.housekeeping;
        },
        referrerOptions: state => {
            if (state.reservation?.referer_choices?.length) {
                return state.reservation?.referer_choices.map(r => ({
                    value: r,
                    text: r,
                }));
            }
        },
        surveyDataOptions: state => {
            if (state.reservation?.survey_data?.choices?.length) {
                return [{ value: -1, text: 'Not set' }].concat(
                    state.reservation?.survey_data?.choices.map(s => ({
                        ...s,
                        value: s.id,
                        text: s.title,
                    }))
                );
            }
        },
        notifySelectedGuests: (state) => state.notifySelectedGuests,
    },

    mutations: {
        gotGuest(state, guest) {
            state.guest = guest;
        },
        gotReservations(state, reservations) {
            state.reservations = reservations;
        },
        gotReservation(state, reservation) {
            state.reservation = reservation;
        },
        gotMetadata(state, response) {
            state.metadata = response.data || {};
        },
        gotDetailsTabTitle(state, title) {
            state.detailsTabTitle = title;
        },
        gotInvoicesTab(state, tab) {
            state.invoicesTab = tab;
        },
        gotStatuses(state, statuses) {
            state.statuses = statuses;
        },
        gotCountries(state, countries) {
            state.countries = countries;
        },
        gotStates(state, states) {
            state.states = states;
        },
        gotProvinces(state, provinces) {
            state.provinces = provinces;
        },
        setNotifySelectedGuests(state, notifySelectedGuests) {
            state.notifySelectedGuests = notifySelectedGuests;
        },
    },

    actions: {
        async fetchGuest({ commit }, { guestId = null }) {
            let guest = {};
            if (guestId) {
                const { data } = await API.get(GUEST_BY_ID_ENDPOINT(guestId));
                guest = data;
            }
            commit('gotGuest', getGuestData(guest));
        },
        async fetchReservations({ commit }, { guestId }) {
            const { data } = await API.get(
                GUEST_RESERVATION_LIST_ENDPOINT(guestId)
            );
            commit('gotReservations', data);
        },
        async fetchReservation({ commit }, { guestId, reservationId }) {
            const { data } = await API.get(
                GUEST_RESERVATION_ENDPOINT(reservationId, guestId)
            );
            commit('gotReservation', data);
        },
        async fetchMetadata({ commit, state }, { force = false } = {}) {
            if (isEmpty(state.metadata) || force) {
                commit('gotMetadata', await API.get('/app/guest/metadata/'));
            }
        },
        async fetchStatuses({ commit }) {
            const { data } = await API.get(GUEST_STATUS_LIST_ENDPOINT());
            commit('gotStatuses', data);
        },
        async fetchCountries({ commit }) {
            const { data } = await API.get(GUEST_COUNTRIES_ENDPOINT());
            commit('gotCountries', data);
        },
        async fetchStates({ commit }, { countryId }) {
            const { data } = await API.get(
                `/asynchronous/get-states/${countryId}`
            );
            commit('gotStates', data.states);
        },
        async fetchProvinces({ commit }, { stateId }) {
            const { data } = await API.get(
                `/asynchronous/get-provinces/${stateId}`
            );
            commit('gotProvinces', data.provinces);
        },
        async getDetailsTabTitle({ commit, dispatch }, { guestId }) {
            await dispatch('fetchGuest', { guestId: guestId });
            await dispatch('fetchStatuses');
        },
        async setReservations({ commit }, reservations) {
            commit('gotReservations', reservations);
        },
        async setReservation({ commit }, reservation) {
            commit('gotReservation', reservation);
        },
        async setGuest({ commit }, guest) {
            if (guest === null) commit('gotGuest', null);
            else commit('gotGuest', getGuestData(guest));
        },
        async setDetailsTabTitle({ commit, state }, title = '') {
            commit('gotDetailsTabTitle', title);
        },
        async setInvoicesTab({ commit }, tab) {
            commit('gotInvoicesTab', tab);
        },
    },
};

function detailsTabTitle(state) {
    var full_name = state.guest.first_name + ' ' + state.guest.last_name;
    if (state.guest.status_id) {
        var currentStatus = state.statuses.find(
            item => state.guest.status_id === item.id
        );
    }
    var statusIcon = null;
    if (currentStatus) {
        statusIcon = currentStatus.is_blocklist
            ? {
                  title: 'Blocklist',
                  src: '/static/images/icons/reservation/blocklist_bg_24.svg',
              }
            : currentStatus.is_vip
            ? {
                  title: 'VIP',
                  src: '/static/images/icons/reservation/vip_bg_24.svg',
              }
            : null;
    }
    return { title: full_name, icon: statusIcon };
}

function getGuestData(guestData) {
    return {
        id: guestData?.id || '',
        email: guestData?.email || '',
        company: guestData?.company || '',
        title: guestData?.title || null,
        first_name: guestData?.first_name || '',
        last_name: guestData?.last_name || '',
        date_of_birth: guestData?.date_of_birth || '',
        has_billing_data: guestData?.has_billing_data || '',
        mobile_phone: guestData?.mobile_phone || '',
        status_id: guestData?.status_id || null,
        address: getAddressData(guestData?.address || {}),
        note: guestData?.note || '',
    };
}

function getAddressData(address) {
    return {
        address1: address?.address1 || '',
        address2: address?.address2 || '',
        country: address?.country || { id: '', name: '', code: '' },
        state: address?.state || { id: '', name: '', code: '' },
        province: address?.province || null,
        city: address?.city || '',
        zip: address?.zip || '',
        work_phone: address?.work_phone || '',
        work_phone_ext: address?.work_phone_ext || '',
        phone: address?.phone || '',
        fax: address?.fax || '',
    };
}
